/**
 * The edition axis and its path-prefix mounts.
 *
 * ChooseChow and ChowDash are separate products served from one origin
 * (see docs/edition_separation.md). A product mounted under a path prefix
 * resolves its edition from that prefix and locks it: `/chowdash/jobs` is
 * ChowDash no matter what a query param, a stored preference, or the sidebar
 * switcher says. The root mount carries no prefix and keeps the `?edition=`
 * behaviour for development.
 *
 * This module holds the edition identity, the prefix table and the path
 * arithmetic, and nothing else. It has no imports at all, and that is a hard
 * requirement rather than tidiness: `vite.config.ts` reads the prefix table to
 * write each mount's landing document, and that file runs in Node under
 * `tsconfig.node.json`, which has neither the DOM lib nor `import.meta.env`.
 * A `window` or `import.meta.env` reference anywhere on this module's import
 * chain fails the typecheck. `lib/edition.ts` holds the runtime edition
 * resolution and re-exports the type from here for its existing callers.
 */

export type Edition = 'choosechow' | 'chowdash' | 'chow'

/**
 * Mount prefix per edition. An edition absent from this table is served from
 * the root mount.
 *
 * Temporary by design: `/chowdash` is a demo mount that puts the rider product
 * on the ChooseChow domain so the client can be shown it without a second
 * deployment. Removing the line retires the mount; nothing else changes.
 */
export const EDITION_BASE_PATHS: Readonly<Record<string, Edition>> = {
  '/chowdash': 'chowdash',
}

/**
 * What a mount says about itself in its served `<head>`.
 *
 * Every `/chowdash` URL is answered with the same document, so this is the only
 * place the mount's title and description exist. It lives here, beside the
 * prefix table, because `vite.config.ts` writes the document from it and that
 * file cannot reach `lib/edition.ts` (see the module note above).
 *
 * `robots: noindex` is not a field because the mount document is always
 * excluded — these are demo surfaces on another product's domain, not pages
 * competing for that domain's identity.
 */
export interface EditionMountMeta {
  /** Product name, matching `EDITION_PROFILE[edition].label`. */
  label: string
  title: string
  description: string
}

export const EDITION_MOUNT_META: Readonly<Record<string, EditionMountMeta>> = {
  '/chowdash': {
    label: 'ChowDash',
    title: 'ChowDash — Rider delivery on the ChooseChow network',
    description:
      'ChowDash riders accept delivery offers, follow active drops and get paid for every completed delivery.',
  },
}

/** The mount prefix a pathname sits under, or `''` for the root mount. */
export function basePathFor(pathname: string): string {
  const path = pathname.split('?')[0].split('#')[0]
  const match = Object.keys(EDITION_BASE_PATHS)
    .filter((prefix) => path === prefix || path.startsWith(`${prefix}/`))
    // Longest prefix first, so a future nested mount wins over its parent.
    .sort((a, b) => b.length - a.length)[0]
  return match ?? ''
}

/** The edition a mount prefix is locked to, or `null` for the root mount. */
export function editionForBasePath(basePath: string): Edition | null {
  return EDITION_BASE_PATHS[basePath] ?? null
}

/** True when the pathname is inside a prefix mount rather than at the root. */
export function isPrefixMount(pathname: string): boolean {
  return basePathFor(pathname) !== ''
}

/**
 * A pathname with its mount prefix removed.
 *
 * React Router strips the basename itself, so this exists for the code that
 * reads `window.location` directly — the SEO head, which must recognise a
 * prefix-mounted page even when that page has no ROUTE_META entry.
 */
export function stripBasePath(pathname: string, basePath: string): string {
  if (basePath === '' || !pathname.startsWith(basePath)) return pathname
  const rest = pathname.slice(basePath.length)
  if (rest === '') return '/'
  return rest.startsWith('/') ? rest : `/${rest}`
}
